import { MouseEvent, TouchEvent, useCallback, useEffect, useRef } from 'react';

type UseSwipeableProps = {
  page: number;
  onPageChange: (page: number) => void;
};

// NEXT_PAGE_THRESHOLD is a number between 0 and 1 that determines for how much the distance is going to be multiplied
// in order to determine the next page
const NEXT_PAGE_THRESHOLD = 1 - 0.7;

export default function useSwipeable({
  page,
  onPageChange,
}: UseSwipeableProps) {
  const ref = useRef<HTMLDivElement>(null);
  const touchStartXRef = useRef<number>(0);
  const swipeableStartXRef = useRef<number>(0);

  const onTouchStart = useCallback((e: TouchEvent) => {
    e.stopPropagation();
    if (!ref.current) return;

    touchStartXRef.current = e.changedTouches[0].clientX;
    swipeableStartXRef.current = ref.current.scrollLeft;
  }, []);

  const onTouchMove = useCallback((e: TouchEvent) => {
    e.stopPropagation();
    if (!ref.current) return;

    const deltaX = e.changedTouches[0].pageX - touchStartXRef.current;
    const maxDeltaX = ref.current.clientWidth;
    const deltaIsHigherThanMax = Math.abs(deltaX) > maxDeltaX;
    if (deltaIsHigherThanMax) return;

    ref.current.scrollLeft = swipeableStartXRef.current - deltaX * 1.5;
  }, []);

  const onMouseMove = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    if (!ref.current) return;

    const isMouseDown = e.buttons === 1;
    if (!isMouseDown) return;

    ref.current.scrollLeft -= e.movementX;
  }, []);

  const onMouseDown = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    if (!ref.current) return;

    swipeableStartXRef.current = ref.current.scrollLeft;
  }, []);

  const onTouchEnd = useCallback(
    (e: TouchEvent | MouseEvent) => {
      e.stopPropagation();
      if (!ref.current) return;

      const currentLeft = ref.current.scrollLeft;
      const containerWidth = ref.current.clientWidth;
      const startPage = Math.round(swipeableStartXRef.current / containerWidth);

      const pages = [
        Math.floor(currentLeft / containerWidth),
        Math.ceil(currentLeft / containerWidth),
      ];

      const prev = pages[0] * containerWidth;
      const curr = pages[1] * containerWidth;

      const prevDistance = Math.abs(prev - currentLeft);
      const currDistance = Math.abs(curr - currentLeft);

      const prevDistanceMultiplier =
        pages[0] === startPage ? 1 : NEXT_PAGE_THRESHOLD;
      const currDistanceMultiplier =
        pages[1] === startPage ? 1 : NEXT_PAGE_THRESHOLD;

      const closestLeft =
        prevDistance * prevDistanceMultiplier <
        currDistance * currDistanceMultiplier
          ? prev
          : curr;
      const closestPage = closestLeft / containerWidth;

      if (closestPage !== page) {
        onPageChange(closestPage);
        return;
      }

      ref.current.scrollTo({
        left: closestLeft,
        behavior: 'smooth',
      });
    },
    [onPageChange, page]
  );

  useEffect(() => {
    if (!ref.current) return;
    ref.current.scrollTo({
      left: page * ref.current.clientWidth,
      behavior: 'smooth',
    });
  }, [page]);

  return {
    ref,
    onTouchStart,
    onTouchMove,
    onMouseMove,
    onTouchEnd,
    onMouseDown,
    onMouseUp: onTouchEnd,
    onMouseLeave: onTouchEnd,
  };
}
